import { useContext, useEffect, useState } from "react";
import { AlertModalCheckIcon } from "../../../../assets";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AppLayout } from "../../../../containers/layout/app";
import { EditTierWrapper } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseButton } from "../../../../components/button/styled";
import { AppContext } from "../../../../context/appContext";
import { retrieveUserProfileService } from "../../../../utils/api/usermanagement/profile/retrieveUser";
import { useQuery } from "@tanstack/react-query";
import { retrieveCountriesService } from "../../../../utils/outbound/country/countries";
import Cookies from "universal-cookie";
import { retrieveAccountTierService } from "../../../../utils/api/configuration/tiermanagement/retrieveTier";
import { updateAccountTierService } from "../../../../utils/api/configuration/tiermanagement/updateTier";

export const EditTier = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [institutionId, setInstitutionId] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState({
        country: " ",
        name: "",
        dailyLimit: "",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    useEffect(() => {
        if (!id) return;
        retrieveAccountTierService(TOKEN, id)
            .then(async (data) => {
                setInstitutionId(data.institution);
                return setFormDetails(data);
            })
            .catch((err) => {
                console.error("Failed to fetch account tier:", err);
            });
    }, [TOKEN, id]);

    const { data: countries } = useQuery({
        queryKey: ['countries', TOKEN],
        queryFn: async () => {
            const user = await retrieveUserProfileService(TOKEN);
            const countriesData = retrieveCountriesService(user?._id);
            return countriesData;
        },
        enabled: !!TOKEN,
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        if (!institutionId) return;
        try {
            const response = await updateAccountTierService(TOKEN, institutionId, id, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to update account tier. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update account tier. ${error.message}`);
            console.error('Failed to update account tier:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Tier-Management"}
            pageTitle={"Update Account Tier"}
        >
            <EditTierWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Tier Updated Successfully"
                    message="Your information has been successfully updated."
                    callToAction="Back to Tiers List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to update tier
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Country
                                </BaseLabel>
                                <BaseSelect
                                    name="country"
                                    fontweight={400}
                                    value={formDetails.country}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Country
                                    </BaseOption>
                                    {countries?.map((country: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={country.id}
                                            >
                                                {country.title}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Tier Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="name"
                                    value={formDetails.name}
                                    placeholder="Enter Tier Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Daily Transfer Limit
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="dailyLimit"
                                    value={formDetails.dailyLimit}
                                    placeholder="Enter Amount"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditTierWrapper>
        </AppLayout>
    )
}